import { RatesDrawer, ReviewQuotedEnrollmentDialog } from "@platform/btx-rates";
import { UploadDocumentsDrawer } from "@platform/propelx-documents";

import { QuoteHistoryDialog } from "../modals/QuoteHistoryDialog";

import type { QuotedEnrollmentReviewState } from "../hooks/useQuotedEnrollmentReview";

// ---------------------------------------------------------------------------
// Trailing dialogs and drawers (quote history, document upload, rates) —
// extracted to keep Quotes' render below the complexity limit
// ---------------------------------------------------------------------------

interface QuotesDialogsAndDrawersProps {
  historyDialogOpen: boolean;
  onHistoryDialogClose: VoidFunction;
  historyCarrierOptions: React.ComponentProps<
    typeof QuoteHistoryDialog
  >["carriers"];
  isLoadingCarriers: React.ComponentProps<
    typeof QuoteHistoryDialog
  >["isLoadingCarriers"];
  selectedHistoryCarrierIds: React.ComponentProps<
    typeof QuoteHistoryDialog
  >["selectedCarrierIds"];
  onHistoryView: React.ComponentProps<typeof QuoteHistoryDialog>["onView"];
  carriers: React.ComponentProps<typeof UploadDocumentsDrawer>["carriers"];
  ecoRfpId: string;
  brokerId: React.ComponentProps<typeof UploadDocumentsDrawer>["brokerId"];
  brokerPortalRfpId: React.ComponentProps<
    typeof UploadDocumentsDrawer
  >["brokerPortalRfpId"];
  showUploadDocumentsDrawer: boolean;
  onCloseUploadDocumentsDrawer: VoidFunction;
  isRatesDrawerOpen: boolean;
  onCloseRatesDrawer: VoidFunction;
  onRateSaved: VoidFunction;
  ratesDrawerBpg: React.ComponentProps<typeof RatesDrawer>["benefitPlanGroup"];
  ratesDrawerBpId: React.ComponentProps<
    typeof RatesDrawer
  >["initialBenefitPlanId"];
  ratesDrawerEnableRateSets: boolean;
  ratesDrawerCoverageType: React.ComponentProps<
    typeof RatesDrawer
  >["coverageType"];
  axiosInstance: React.ComponentProps<typeof RatesDrawer>["axiosInstance"];
  enrollmentReview: QuotedEnrollmentReviewState;
}

export const QuotesDialogsAndDrawers = ({
  historyDialogOpen,
  onHistoryDialogClose,
  historyCarrierOptions,
  isLoadingCarriers,
  selectedHistoryCarrierIds,
  onHistoryView,
  carriers,
  ecoRfpId,
  brokerId,
  brokerPortalRfpId,
  showUploadDocumentsDrawer,
  onCloseUploadDocumentsDrawer,
  isRatesDrawerOpen,
  onCloseRatesDrawer,
  onRateSaved,
  ratesDrawerBpg,
  ratesDrawerBpId,
  ratesDrawerEnableRateSets,
  ratesDrawerCoverageType,
  axiosInstance,
  enrollmentReview,
}: QuotesDialogsAndDrawersProps): React.JSX.Element => (
  <>
    <QuoteHistoryDialog
      open={historyDialogOpen}
      onClose={onHistoryDialogClose}
      carriers={historyCarrierOptions}
      isLoadingCarriers={isLoadingCarriers}
      selectedCarrierIds={selectedHistoryCarrierIds}
      onView={onHistoryView}
    />
    <UploadDocumentsDrawer
      carriers={carriers}
      ecoRfpId={ecoRfpId}
      brokerId={brokerId}
      brokerPortalRfpId={brokerPortalRfpId}
      open={showUploadDocumentsDrawer}
      onClose={onCloseUploadDocumentsDrawer}
    />
    <RatesDrawer
      open={isRatesDrawerOpen}
      onClose={onCloseRatesDrawer}
      onSaveSuccess={onRateSaved}
      benefitPlanGroup={ratesDrawerBpg}
      initialBenefitPlanId={ratesDrawerBpId}
      axiosInstance={axiosInstance}
      planType="proposal"
      enableRateSets={ratesDrawerEnableRateSets}
      coverageType={ratesDrawerCoverageType}
    />
    <ReviewQuotedEnrollmentDialog
      review={enrollmentReview.review}
      carrierName={enrollmentReview.carrierName}
      isApplying={enrollmentReview.isApplying}
      onApply={enrollmentReview.apply}
      onCancel={enrollmentReview.close}
    />
  </>
);
